import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useEntityForm } from '@/shared/lib/useEntityForm';
import { useRouteId } from '@/shared/lib/useRouteId';
import { DetailLayout } from '@/shared/ui/DetailLayout';
import { FileUploadField } from '@/shared/ui/FileUploadField';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { MultiSelect } from '@/shared/ui/MultiSelect';
import { SaveBar } from '@/shared/ui/SaveBar';
import { TextField } from '@/shared/ui/TextField';
import type { UploadedFile } from '@/shared/api/upload';
import {
  ARTICLE_STATUS_OPTIONS,
  articleImageFiles,
  articlePreviewFiles,
  articleToValues,
  deleteArticleImage,
  deleteArticlePreview,
  emptyArticle,
  fetchArticle,
  fetchCategoryOptions,
  fetchTagOptions,
  isRssCategory,
  saveArticle,
  uploadArticleImage,
  uploadArticlePreview,
  type ArticleItem,
  type ArticleValues,
} from './api';

const SELECT_WIDTH = 300;
const OG_TITLE_MAX_LENGTH = 60;

/** Pictures are bound to an existing article, so they are only available when editing. */
const ArticleMedia = ({ articleId, article }: { articleId: number; article: ArticleItem }) => {
  const [preview, setPreview] = useState<UploadedFile[]>(() => articlePreviewFiles(article));
  const [images, setImages] = useState<UploadedFile[]>(() => articleImageFiles(article));

  return (
    <>
      <FileUploadField
        label="Картинка для заголовка"
        files={preview}
        previewWidth={140}
        upload={uploadArticlePreview(articleId)}
        remove={deleteArticlePreview}
        onChange={setPreview}
      />
      <FileUploadField
        label="Картинки для тела статьи"
        files={images}
        multiple
        showUrl
        upload={uploadArticleImage(articleId)}
        remove={deleteArticleImage}
        onChange={setImages}
      />
    </>
  );
};

type FormProps = { id?: number; initial: ArticleValues; article?: ArticleItem };

const ArticleForm = ({ id, initial, article }: FormProps) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const categories = useQuery({ queryKey: ['publication-category-options'], queryFn: fetchCategoryOptions });
  const tags = useQuery({ queryKey: ['publication-tag-options'], queryFn: fetchTagOptions });

  const form = useEntityForm(
    initial,
    (values) => saveArticle(id, values),
    (res) => {
      void qc.invalidateQueries({ queryKey: ['publications-articles'] });
      const createdId = res.id ?? res.article?.id;
      if (!id && createdId) navigate(`/publications/article/edit/${createdId}`, { replace: true });
    },
  );
  const { values, errors, setField } = form;

  const changeCategory = (categoryId: string) => {
    setField('categoryId', categoryId, 'category_id');
    setField('rssView', isRssCategory(categoryId), 'rss_view');
  };

  return (
    <>
      <TextField
        label="Заголовок *"
        placeholder="Заголовок"
        value={values.title}
        errors={errors.title}
        onChange={(v) => setField('title', v)}
      />
      <FormField label="Статус *" errors={errors.status_id}>
        <FilterSelect
          options={ARTICLE_STATUS_OPTIONS}
          value={values.statusId}
          isClearable={false}
          width={SELECT_WIDTH}
          onChange={(v) => setField('statusId', v, 'status_id')}
        />
      </FormField>
      <TextField
        label="ЧПУ"
        placeholder="ЧПУ"
        value={values.slug}
        errors={errors.slug}
        onChange={(v) => setField('slug', v)}
      />
      <FormField label="Категория *" errors={errors.category_id}>
        <FilterSelect
          options={categories.data ?? []}
          value={values.categoryId}
          isLoading={categories.isLoading}
          isClearable={false}
          width={SELECT_WIDTH}
          onChange={changeCategory}
        />
      </FormField>
      <FormField label="Теги" errors={errors.tag_id ?? errors.currentTags}>
        <MultiSelect
          options={tags.data ?? []}
          value={values.tagIds}
          isLoading={tags.isLoading}
          width={SELECT_WIDTH}
          onChange={(v) => setField('tagIds', v, 'tag_id')}
        />
      </FormField>
      <TextField
        label="Дата публикации"
        type="datetime-local"
        maxWidth={SELECT_WIDTH}
        value={values.publishedTime}
        errors={errors.published_time}
        onChange={(v) => setField('publishedTime', v, 'published_time')}
      />
      {id && article && <ArticleMedia articleId={id} article={article} />}
      <TextField
        label="Заголовок OpenGraph"
        placeholder="Заголовок OpenGraph"
        multiline
        rows={3}
        maxLength={OG_TITLE_MAX_LENGTH}
        value={values.ogTitle}
        errors={errors.og_title}
        onChange={(v) => setField('ogTitle', v, 'og_title')}
      />
      <TextField
        label="Описание OpenGraph"
        placeholder="Описание OpenGraph"
        multiline
        rows={3}
        value={values.ogDescription}
        errors={errors.og_description}
        onChange={(v) => setField('ogDescription', v, 'og_description')}
      />
      <FormField label="Экспорт в RSS" errors={errors.rss_view}>
        <input
          type="checkbox"
          checked={values.rssView}
          disabled={!isRssCategory(values.categoryId)}
          onChange={(e) => setField('rssView', e.target.checked, 'rss_view')}
        />
      </FormField>
      <TextField
        label="Ссылка для редиректа"
        placeholder="URL"
        value={values.redirect}
        errors={errors.redirect}
        onChange={(v) => setField('redirect', v)}
      />
      <TextField
        label="Текст (HTML)"
        multiline
        rows={20}
        maxWidth={1000}
        value={values.body}
        errors={errors.body}
        onChange={(v) => setField('body', v)}
      />
      <SaveBar isSaving={form.isSaving} saved={form.saved} errors={errors} onSave={form.save} />
    </>
  );
};

export const ArticleCreatePage = () => (
  <DetailLayout title="Создание новости">
    <ArticleForm initial={emptyArticle()} />
  </DetailLayout>
);

export const ArticleEditPage = () => {
  const id = useRouteId();
  const { data, isLoading, error } = useQuery({ queryKey: ['publications-article', id], queryFn: () => fetchArticle(id) });

  return (
    <DetailLayout title="Редактирование новости" isLoading={isLoading} error={error}>
      {data && <ArticleForm key={id} id={id} initial={articleToValues(data)} article={data} />}
    </DetailLayout>
  );
};
